import { SelectOption } from '@common/types/common';
import { UserList } from '@common/types/user';
import { Flag } from '@components/display/images';
import { BorderlessTableCell, Table } from '@components/display/tables';
import { IconButton } from '@components/utility/buttons';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Chip, Grid2, Tooltip, Typography } from '@mui/material';
import { getRoleName } from '@utils/user';
import { useCallback } from 'react';
import { memberTableHeaders } from '../constants';

type MembersTableProps = {
    members: UserList[];
    sort: string | null;
    page: number;
    rowsPerPage: number;
    rowsPerPageOptions: SelectOption<number>[];
    totalCount: number;
    selectedRows: number[];
    isAdmin: boolean;
    loading: boolean;
    isSelectAllLoading: boolean;
    onSortClick: (column: string) => void;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
    onSelectAll: (checked: boolean) => void;
    onRowCheck: (member: UserList) => void;
    onRowOptionClick: (member: UserList) => void;
};

export const MembersTable = ({
    members,
    sort,
    page,
    rowsPerPage,
    rowsPerPageOptions,
    totalCount,
    selectedRows,
    isAdmin,
    onSortClick,
    onPageChange,
    onRowsPerPageChange,
    onSelectAll,
    onRowCheck,
    loading,
    isSelectAllLoading,
    onRowOptionClick,
}: MembersTableProps) => {
    const renderRow = useCallback(
        (member: UserList) => (
            <>
                <BorderlessTableCell>
                    <Tooltip
                        title={`${
                            member.first_name && member.last_name
                                ? `${member.first_name} ${member.last_name}`
                                : member.full_name || '-'
                        }, ${getRoleName(member.role)}`}
                        arrow
                    >
                        <Grid2 container alignItems="center">
                            <Grid2 size={2}>
                                <Flag flag={member.country} className="rounded-lg" />
                            </Grid2>
                            <Grid2 size={10}>
                                <Typography
                                    color="neutral-800"
                                    fontWeight={500}
                                    fontSize={14}
                                    minHeight="1.5em"
                                    className="line-clamp-2 text-ellipsis"
                                >
                                    {member.first_name && member.last_name
                                        ? `${member.first_name} ${member.last_name}`
                                        : member.full_name || '-'}
                                </Typography>
                            </Grid2>
                            <Grid2 size={10} offset={2}>
                                <Typography color="neutral-600" fontSize={14}>
                                    {getRoleName(member.role)}
                                </Typography>
                            </Grid2>
                        </Grid2>
                    </Tooltip>
                </BorderlessTableCell>
                <BorderlessTableCell>
                    <Typography color="neutral-800" fontSize={14} fontWeight={500} className="truncate">
                        {member.email}
                    </Typography>
                </BorderlessTableCell>
                <BorderlessTableCell>
                    {member.company_name && (
                        <Chip
                            label={member.company_name}
                            size="small"
                            sx={{
                                backgroundColor: '#EEFAFF',
                                border: '1px solid #89BEEC73',
                                color: '#2785D7',
                            }}
                        />
                    )}
                </BorderlessTableCell>
            </>
        ),
        []
    );

    return (
        <Table
            id="members"
            headers={memberTableHeaders}
            sort={sort}
            data={members}
            querysetIds={members.map((member) => member.id)}
            renderRow={renderRow}
            emptyListTxt="There are currently no members to list"
            onSortClick={onSortClick}
            page={page}
            onPageChange={onPageChange}
            selectedItems={selectedRows}
            onSelectAll={(event) => onSelectAll(event.target.checked)}
            onRowCheckClick={onRowCheck}
            rowOptions={{
                data: (member) => (
                    <IconButton
                        onClick={() => (member ? onRowOptionClick(member) : undefined)}
                        size="small"
                        color="signal-warning"
                        className="border border-solid border-signal-warning p-[3px]"
                        disabled={!isAdmin}
                    >
                        <DeleteOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                ),
            }}
            count={totalCount}
            isLoading={loading}
            isSelectAllLoading={isSelectAllLoading}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={rowsPerPageOptions}
            setRowsPerPage={onRowsPerPageChange}
            isSelectDisabled={!isAdmin}
            separator="gray"
            selectable
            truncateCells
            scrollOnPageChange
            getHref={(member) => member.id.toString()}
            fillMissingRows={false}
        />
    );
};
